import {orderBy} from 'lodash';
import React, {FC, Fragment} from 'react';
import styled from 'styled-components';

import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {ConversationSortPropertiesEnum} from '../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {
  TeammateAvailabilityStatusesEnum,
  TeammateModel,
} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {getTeammates} from '../../../../../libs/shared/core/src/store/data/teammates/commonTeammatesSelectors';
import {getHasTeammateBusyStatusFF} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {AppContentTypesEnum} from '../../../../../libs/shared/core/src/store/panels/app/commonAppPanelState';
import {AnalyticsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Skeleton} from '../../../../../libs/shared/ui/src/components/utility/skeleton';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {TeammateAvatarWithStatus} from '../../../../components/common/avatars/teammateAvatarWithStatus';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {getOnlineStatusForTeammate} from '../../../../helpers/models/teammateHelpers';
import {LiveDashboardContentTeammateAssignedCountModel} from '../../../../models/api/liveDashboardContentModel';
import {CellsSortDirectionsEnum} from '../../../../models/api/sortOptionModel';
import {InboxesFiltersEnum, TeammatesFoldersEnum} from '../../../../models/url/models/inboxesUrlModel';
import {getOnlineTeammateIdsSet} from '../../../../store/data/teammates/onlineTeammatesSelectors';
import {getFindTeammate} from '../../../../store/data/teammates/teammatesSelectors';
import {pushParametersAndFocusAction} from '../../../../store/panels/app/appPanelRouteActions';
import {InboxesParametersTypesEnum} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {emptyInboxesSelection} from '../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {renderNumericalValue} from '../../../helpers/metricRenderers/numericalMetricRenderer';
import {ReportElementIdsEnum} from '../../../helpers/reportHelpers/reportTypes';
import {ViewInInbox} from '../viewInInbox';
import {SelectableStatusesEnum} from './liveDashboardAssignedByTeammateStatusDropdown';

/*
 * Props.
 */

interface LiveDashboardTeammateAssignedByTeammateRowsProps {
  panelId: AnalyticsPanelId;
  isSortingAscending: boolean;
  selectedStatuses: ReadonlySet<SelectableStatusesEnum>;
  teammateAssignedCounts: ReadonlyArray<LiveDashboardContentTeammateAssignedCountModel> | undefined;
}

/*
 * Styles.
 */

const StyledAssignedByTeammateGridDiv = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-auto-rows: 24px;
  gap: 8px;
  justify-items: start;
  align-items: center;
  padding: 0 8px;
`;

const StyledAssignedCountWrapperDiv = styled.div`
  justify-self: end;
`;

const StyledEllipsisSpan = styled.div`
  width: 100%;
  ${ellipsis()};
`;

/*
 * Components.
 */

const AssignedCount: FC<{
  teammateAssignedCount: LiveDashboardContentTeammateAssignedCountModel;
  panelId: AnalyticsPanelId;
}> = ({teammateAssignedCount, panelId}) => (
  <ViewInInbox
    actionBuilder={() =>
      pushParametersAndFocusAction(panelId, {
        contentType: AppContentTypesEnum.INBOXES,
        parameters: {
          type: InboxesParametersTypesEnum.TEAMMATE,
          teammateId: teammateAssignedCount.teammateId,
          sortDirection: CellsSortDirectionsEnum.DESCENDING,
          sortProperty: ConversationSortPropertiesEnum.BUMPED_AT,
          selection: emptyInboxesSelection,
          refinement: undefined,
          filters: [InboxesFiltersEnum.OPEN],
          folder: TeammatesFoldersEnum.ASSIGNED_SHARED,
        },
      })
    }
    trackingPayload={{
      report_section: ReportElementIdsEnum.LIVE_DASHBOARD_ASSIGNED_BY_TEAMMATE,
      resource_type: 'teammate',
      resource_id: teammateAssignedCount.teammateId,
      clicked_value: teammateAssignedCount.assignedCount,
    }}
    shouldShowAssignedConfirmationDialog
  >
    {renderNumericalValue({value: teammateAssignedCount.assignedCount, isZeroValid: true})}
  </ViewInInbox>
);

const skeletonRow = (
  <>
    <Skeleton height="24px" width="24px" borderRadius="1000px" />
    <Skeleton height="16px" width="93px" />
    <Skeleton height="16px" width="18px" />
  </>
);

export const LiveDashboardTeammateAssignedByTeammateRows: FC<
  LiveDashboardTeammateAssignedByTeammateRowsProps
> = ({isSortingAscending, selectedStatuses, teammateAssignedCounts, panelId}) => {
  const teammates = useAppSelector(getTeammates);
  const onlineTeammateIdsSet = useAppSelector(getOnlineTeammateIdsSet);
  const findTeammate = useAppSelector(getFindTeammate);
  const hasTeammateBusyStatus = useAppSelector(getHasTeammateBusyStatusFF);

  if (!isDefined(teammateAssignedCounts)) {
    return (
      <StyledAssignedByTeammateGridDiv>
        {skeletonRow}
        {skeletonRow}
        {skeletonRow}
        {skeletonRow}
        {skeletonRow}
      </StyledAssignedByTeammateGridDiv>
    );
  }

  const filteredTeammateAssignedCounts = teammateAssignedCounts.filter((teammateAssignedCount) => {
    const teammate = findTeammate(teammateAssignedCount.teammateId);
    return isTeammatePartOfSelection(teammate, selectedStatuses, onlineTeammateIdsSet, hasTeammateBusyStatus);
  });

  const sortedAssignedByTeammate = orderBy(
    filteredTeammateAssignedCounts,
    'assignedCount',
    isSortingAscending ? 'asc' : 'desc',
  );

  return (
    <StyledAssignedByTeammateGridDiv>
      {sortedAssignedByTeammate.map((teammateAssignedCount) => {
        const teammate = teammates[teammateAssignedCount.teammateId];

        if (!teammate) {
          return null;
        }

        return (
          <Fragment key={teammateAssignedCount.teammateId}>
            <TeammateAvatarWithStatus
              teammate={teammate}
              size={VisualSizesEnum.MEDIUM}
              onlineStatus={getOnlineStatusForTeammate(teammate, onlineTeammateIdsSet)}
              teammateStatus={teammate.availabilityStatus}
            />
            <StyledEllipsisSpan>{buildTeammateDisplayName(teammate)}</StyledEllipsisSpan>
            <StyledAssignedCountWrapperDiv>
              <AssignedCount teammateAssignedCount={teammateAssignedCount} panelId={panelId} />
            </StyledAssignedCountWrapperDiv>
          </Fragment>
        );
      })}
    </StyledAssignedByTeammateGridDiv>
  );
};

/*
 * Helpers.
 */

function isTeammatePartOfSelection(
  teammate: TeammateModel | undefined,
  selectedStatuses: ReadonlySet<SelectableStatusesEnum>,
  onlineTeammateIdsSet: ReadonlySet<number> | undefined,
  hasTeammateBusyStatus: boolean,
) {
  if (!teammate) {
    return true;
  }

  if (!hasTeammateBusyStatus) {
    if (!teammate.isAvailable) {
      return selectedStatuses.has(SelectableStatusesEnum.OUT_OF_OFFICE);
    }

    if (!onlineTeammateIdsSet?.has(teammate.id)) {
      return selectedStatuses.has(SelectableStatusesEnum.OFFLINE_OR_BUSY);
    }

    return selectedStatuses.has(SelectableStatusesEnum.AVAILABLE);
  }

  switch (teammate.availabilityStatus) {
    case TeammateAvailabilityStatusesEnum.AVAILABLE:
      return selectedStatuses.has(SelectableStatusesEnum.AVAILABLE);

    case TeammateAvailabilityStatusesEnum.BUSY:
      return selectedStatuses.has(SelectableStatusesEnum.OFFLINE_OR_BUSY);

    case TeammateAvailabilityStatusesEnum.OUT_OF_OFFICE:
      return selectedStatuses.has(SelectableStatusesEnum.OUT_OF_OFFICE);

    default:
      // Shouldn't happen, show teammate if it does.
      return true;
  }
}
